import * as R from 'rambdax'
import {useReducer, useEffect, useRef} from 'react'
import {logger} from 'tizra'
import {useEqualBy} from './useEqualBy'

const log = logger('quickstart/hooks/useObserver')

const reducer = (state, action) => {
  switch (action.type) {
    case 'observer': {
      const {observer} = action
      if (state.observer) {
        state.observer.disconnect()
      }
      if (state.node) {
        observer.observe(state.node)
      }
      return {...state, observer}
    }

    case 'node': {
      const {node} = action
      if (state.node === node) {
        return state
      }
      if (state.observer) {
        state.observer.disconnect()
        if (node) {
          state.observer.observe(node)
        }
      }
      return {...state, node}
    }

    case 'entry': {
      const {entry} = action
      if (state.entry === entry) {
        return state
      }
      return {...state, entry}
    }

    default:
      throw new Error()
  }
}

/**
 * Use a callback ref with an observer (IntersectionObserver, ResizeObserver).
 */
export const useObserver = (Observer, options) => {
  options = useEqualBy(R.equals, options)
  const [state, dispatch] = useReducer(reducer, {})

  useEffect(() => {
    const observer = new Observer(entries => {
      if (entries.length) {
        dispatch({type: 'entry', entry: entries[entries.length - 1]})
      }
    }, options)
    dispatch({type: 'observer', observer})
    return () => {
      observer.disconnect()
    }
  }, [Observer, options])

  // This callback ref, as opposed to a normal useRef, will be called whenever
  // the node comes or goes. That way we can update the observer accordingly.
  const callbackRef = useRef(node => {
    dispatch({type: 'node', node})
  }).current

  return [state.entry, callbackRef]
}
